import React, { FC, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { deepEqual } from 'fast-equals';
// fetch
import { updateNewsItem, createNewsItem, getNewsItem, createNewsFile } from '../../../../fetch';
import { INews, INewsDetail } from '../../../../models';
import { NewsItemModalContent } from './news-item-modal-content';
import { formatDBFriendlyDateTime } from '../../../../helpers/format';

interface IAddEditNewsItemModal {
  open: boolean;
  onClose: () => void;
  currentNewsItem?: INews | null;
  setCurrentNewsItem: React.Dispatch<React.SetStateAction<INews>>;
  fetchNewsItems: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
}

const Schema = Yup.object().shape({
  category: Yup.string().required('Required'),
  subject: Yup.string().required('Required'),
  message: Yup.string().required('Required'),
  newsItemType: Yup.string().required('Required'),
  displayDate: Yup.string().required('Required'),
  externalUrl: Yup.string().url('Must be a valid URL'),
  takeDownDate: Yup.string(),
  customerPageId: Yup.string(),
  usernames: Yup.array(Yup.string()),
  vendorIds: Yup.array(Yup.string()),
  homeownerIds: Yup.array(Yup.string()),
  salesAgentIds: Yup.array(Yup.string()),
  subcontractorTypeIds: Yup.array(Yup.string()),
  visibilities: Yup.array(Yup.string())
    .min(1)
    .required('Please select a target for news item'),
  fileData: Yup.mixed().when('newsItemType', {
    is: 'AttachedFile',
    then: Yup.mixed().required('File is required'),
    otherwise: Yup.string().notRequired()
  }),
  // Custom test for SpecificUsers visibility selection
  atLeastOneSelection: Yup.mixed().test(
    'at-least-one-selection',
    'Select at least one Homeowner, Vendor, Employee, Sales Agent, or Subcontractor.',
    function (_, context) {
      const { visibilities, usernames, vendorIds, homeownerIds, salesAgentIds, subcontractorTypeIds } = context.parent;
      if (visibilities && visibilities.includes('SpecificUsers')) {
        return (
          (usernames && usernames.length > 0) ||
          (vendorIds && vendorIds.length > 0) ||
          (homeownerIds && homeownerIds.length > 0) ||
          (salesAgentIds && salesAgentIds.length > 0) ||
          (subcontractorTypeIds && subcontractorTypeIds.length > 0)
        );
      }
      return true;
    }
  )
});

enum NewsItemType {
  MessageOnly = 'MessageOnly',
  CustomPage = 'CustomPage',
  ExternalUrl = 'ExternalUrl',
  AttachedFile = 'AttachedFile'
}
export const AddEditNewsItemModal: FC<IAddEditNewsItemModal> = ({
  open,
  onClose,
  currentNewsItem,
  setCurrentNewsItem,
  fetchNewsItems,
  setIsLoading,
  isLoading
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [newsDetail, setNewsDetail] = useState<INewsDetail>();
  const [file, setFile] = useState<any>();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await getNewsItem(currentNewsItem.newsItemId);

      setNewsDetail(response);
    } catch (err) {
      enqueueSnackbar(err?.Detail ?? 'Error getting news item.', {
        variant: 'error'
      });
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (currentNewsItem && open) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNewsItem, open]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          category: currentNewsItem?.category ?? '',
          subject: currentNewsItem?.subject ?? '',
          message: currentNewsItem?.message ?? '',
          displayDate: currentNewsItem?.displayDate
            ? new Date(currentNewsItem?.displayDate)
            : new Date(),
          takeDownDate: currentNewsItem?.takeDownDate
            ? new Date(currentNewsItem?.takeDownDate)
            : null,
          customPageId: currentNewsItem?.customPageId ?? '',
          visibilities: newsDetail?.visibilities.map(v => v.value) ?? [],
          usernames: newsDetail?.usernames ?? [],
          subcontractorTypeIds: newsDetail?.subcontractorTypeIds ?? [],
          vendorIds: newsDetail?.vendors?.map(v => v.vendorId) ?? [],
          homeownerIds: newsDetail?.homeownerIds ?? [],
          salesAgentIds: newsDetail?.salesAgentIds ?? [],
          newsItemType: currentNewsItem?.newsItemType ?? '',
          externalUrl: currentNewsItem?.externalUrl || newsDetail?.externalUrl || '',
          fileData: file ?? ''
        }}
        validationSchema={Schema}
        onSubmit={async (values, actions) => {
          const data = {
            ...values,
            displayDate: formatDBFriendlyDateTime(values.displayDate),
            takeDownDate: formatDBFriendlyDateTime(values.takeDownDate),
            customPageId: values.customPageId ? Number(values.customPageId) : null,
            type: values.newsItemType ?? '',
            fileData: values.fileData
          };

          // Match BE validation
          if (
            values.newsItemType === NewsItemType.MessageOnly ||
            values.newsItemType === NewsItemType.AttachedFile
          ) {
            data.customPageId = null;
            data.externalUrl = '';
          }
          if (values.newsItemType === NewsItemType.MessageOnly) {
            data.customPageId = null;
          }
          if (values.newsItemType === NewsItemType.CustomPage) {
            data.externalUrl = null;
          }

          const isFileAttachmentType = values.newsItemType === NewsItemType.AttachedFile;
          try {
            // Edit
            if (currentNewsItem) {
              await updateNewsItem(currentNewsItem?.newsItemId, data);
              if (isFileAttachmentType && values.fileData) {
                // if the news item is an attachedFile type, create the file

                if (typeof values?.fileData !== 'string') {
                  await createNewsFile(currentNewsItem?.newsItemId, values?.fileData);
                }
              }
              enqueueSnackbar('Successfully updated news item!', {
                variant: 'success'
              });
            }
            // Create
            if (!currentNewsItem) {
              const res = await createNewsItem(data);
              if (res && isFileAttachmentType) {
                // if the news item is an attachedFile type, create the file
                await createNewsFile(res, values?.fileData);
              }
              enqueueSnackbar('Successfully created news item!', {
                variant: 'success'
              });
            }

            onClose();
            setFile('');
            setNewsDetail(null);
            fetchNewsItems();
            actions.resetForm();
          } catch (error: any) {
            enqueueSnackbar(error?.Detail ?? 'Error saving news item, please try again.', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          errors,
          setFieldValue,
          handleSubmit,
          dirty,
          isValid,
          initialValues
        }) => {
          const handleClose = () => {
            if (!deepEqual(initialValues, values)) {
              const result = window.confirm(
                'You have unsaved changes, are you sure you want to exit?'
              );
              if (result) {
                resetForm();
                setNewsDetail(null);
                setCurrentNewsItem(null);
                setFile('');
                onClose();
              } else {
                return;
              }
            } else {
              resetForm();
              setNewsDetail(null);
              setCurrentNewsItem(null);
              setFile('');
              onClose();
            }
          };
          return (
            <>
              <NewsItemModalContent
                open={open && !isLoading}
                handleClose={handleClose}
                currentNewsItem={newsDetail}
                setFieldValue={setFieldValue}
                values={values}
                errors={errors}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                dirty={dirty}
                isValid={isValid}
                isLoadingModalContent={isLoading}
                file={file}
                setFile={setFile}
                resetForm={resetForm}
              />
            </>
          );
        }}
      </Formik>
    </>
  );
};
